import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useCase } from '@/features/cases/api/useCase'
import { placeName } from '@/lib/countries'

/* The case a payment belongs to, as a link — «Работа AB-3 · Мерв → Ташкент»
 * (UX walkthrough F-2, QW-14).
 *
 * Read through the case's own hook, keyed at `cases.detail(caseId)`. Where
 * `PaymentAllocations` reads the case too — an OUTBOUND payment, for a reader
 * who may allocate — the two share that entry; otherwise this read is its own.
 *
 * Rendered ONLY on a successful read, and that is the point rather than a
 * shortcut. A reader whose scope does not reach the case — Finance can read a
 * payment on a case it is not on — receives a 404, and the honest rendering of
 * that is no line: an ErrorState here would put a failure box over a payment
 * the reader may perfectly well read, and a skeleton would block nothing that
 * needs blocking. The lane is composed the way `CaseCard` composes it: each
 * end dropped rather than dashed when absent. */
export function PaymentCaseLink({ caseId }: { caseId: string }) {
  const { t, i18n } = useTranslation()
  const { data } = useCase(caseId)
  if (!data) return null

  const origin = placeName(data.origin, i18n.language)
  const destination = placeName(data.destination, i18n.language)
  const lane = origin && destination ? `${origin} → ${destination}` : origin || destination

  return (
    <Link
      to={`/cases/${caseId}`}
      className="text-text-link hover:text-text-link-hover mb-1.5 block w-fit text-[12.5px]"
    >
      {[t('finance.payments.caseLine', { number: data.number }), lane]
        .filter(Boolean)
        .join(' · ')}
    </Link>
  )
}
